import { Title, Card, Text } from '@constants/Themed';
import toTitleCase from '@hooks/toTitleCase';
import React, { useState, useEffect, useRef, forwardRef, useMemo } from 'react';
import { ScrollView, View, Pressable, FlatList, Platform, Dimensions } from 'react-native';
import Draggable from 'react-native-draggable';
import DraggableFunnelItem from './DraggableFunnelItem';
import parseByDelimiter from '@utils/parseByDelimiter';
import { debounce } from 'lodash';
import * as Animatable from 'react-native-animatable';
import FunnelListHeader from './FunnelListHeader';
import DefaultCustomer from '@navigation/screens/MidLevel/DataScreen/components/DefaultCustomer';
import { SizableText, XStack, YStack } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';


interface SalesFunnelListProps {
  salesFunnel: any,
  clients: any[],
  ref: any,
  viewMode?: 'all' | 'single',
  selectedStageIndex?: number | null,
  onMoveLead?: (leadId: string, fromStage: string, toStage: string) => void,
  onUpdateLead?: (leadId: string, leadData: any) => void,
  onDeleteLead?: (leadId: string) => void,
  onAddStage?: (stageData: any) => void,
  onDeleteStage?: (stageId: string) => void
}

interface FunnelListHandle {
  ref: FlatList
}

const SalesFunnelList = forwardRef<FunnelListHandle, SalesFunnelListProps>((props, ref) => {
  const { salesFunnel, clients, viewMode = 'all', selectedStageIndex, onMoveLead, onUpdateLead, onDeleteLead } = props;
  const theme = themeSelector();
  const [listClients, setClients] = useState(clients);
  const stages = salesFunnel?.stages;
  const [showHeaderDetails, setShowHeaderDetails] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [timerId, setTimerId] = useState(null);
  const [draggedOverStage, setDraggedOverStage] = useState<number | null>(null);

  useEffect(() => {
    return () => {
      clearTimeout(timerId);
      setClients([]);
    };
  }, [timerId]);

  // Organize clients by stage using audience_data directly
  const clientsByStage = useMemo(() => {
    const organized: { [stageId: string]: any[] } = {};

    console.log('Organizing clients by stage:', {
      listClients,
      stages,
      audienceData: salesFunnel?.audience_data
    });

    stages?.forEach((stage, stageIndex) => {
      organized[stage._id] = [];
    });

    // Use audience_data directly from salesFunnel if available
    if (salesFunnel?.audience_data && Array.isArray(salesFunnel.audience_data)) {
      salesFunnel.audience_data.forEach((stageLeads, stageIndex) => {
        const stageId = stages?.[stageIndex]?._id;
        if (stageId && Array.isArray(stageLeads)) {
          organized[stageId] = stageLeads.map(lead => ({
            ...lead,
            stageIndex,
            stageId
          }));
          console.log(`Added ${stageLeads.length} leads to stage ${stageIndex} (${stageId})`);
        }
      });
    } else if (Array.isArray(listClients) && listClients.length > 0) {
      // Fallback to listClients if audience_data is not available
      listClients.forEach((client, index) => {
        let stageId = client.stageId;

        // If no stageId, try to determine from stageIndex
        if (!stageId && typeof client.stageIndex === 'number') {
          stageId = stages?.[client.stageIndex]?._id;
        }

        // Fallback: distribute evenly across stages if no stage info
        if (!stageId) {
          const fallbackStageIndex = index % (stages?.length || 1);
          stageId = stages?.[fallbackStageIndex]?._id;
          console.log(`Fallback: assigning client ${index} to stage ${fallbackStageIndex}`);
        }

        if (stageId && organized[stageId]) {
          organized[stageId].push(client);
          console.log(`Added client to stage ${stageId}:`, client.data?.name);
        }
      });
    }

    console.log('Final organized clients:', organized);
    return organized;
  }, [listClients, stages, salesFunnel?.audience_data]);

  const handleDragEnd = (leadId: string, fromStageId: string, toStageIndex: number) => {
    const toStage = stages?.[toStageIndex];
    if (toStage && onMoveLead) {
      onMoveLead(leadId, fromStageId, toStage._id);
    }
    setDraggedOverStage(null);
  };

  const getItemLayout = (data, index) => ({
    length: 70,
    offset: 70 * index,
    index,
  });

  // Modern Empty State Component
  const EmptyStateComponent = useMemo(() => (
    <Card
      style={{
        margin: 16,
        padding: 20,
        alignItems: 'center',
        backgroundColor: theme ? "#ffffff" : "#1e293b",
        borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
      }}
    >
      <MaterialIcons
        name="people-outline"
        size={48}
        color={theme ? "#64748b" : "#94a3b8"}
        style={{ marginBottom: 12 }}
      />
      <SizableText style={{
        textAlign: 'center',
        fontSize: 16,
        fontWeight: '600',
        color: theme ? "#1e293b" : "#f1f5f9",
        marginBottom: 6
      }}>
        No Leads in Campaign
      </SizableText>
      <SizableText style={{
        textAlign: 'center',
        fontSize: 12,
        color: theme ? "#64748b" : "#94a3b8",
        lineHeight: 18
      }}>
        Add leads to your campaign to start tracking their progress through the sales funnel
      </SizableText>
    </Card>
  ), [theme]);

  // Filter stages based on view mode
  const stagesToShow = useMemo(() => {
    if (viewMode === 'single' && typeof selectedStageIndex === 'number') {
      return stages?.filter((_, index) => index === selectedStageIndex) || [];
    }
    return stages || [];
  }, [stages, viewMode, selectedStageIndex]);

  return (
    <YStack style={{ flex: 1, backgroundColor: 'transparent' }}>
      {/* Column-based Funnel Layout */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingHorizontal: 20 }}
      >
        <XStack gap={16} alignItems="flex-start">
          {stagesToShow.map((stage, stageIndex) => {
            // Map the filtered index back to the original index
            const originalStageIndex = viewMode === 'single' && typeof selectedStageIndex === 'number'
              ? selectedStageIndex
              : stageIndex;

            return (
              <FunnelColumn
                key={stage._id}
                stage={stage}
                stageIndex={originalStageIndex}
                leads={clientsByStage[stage._id] || []}
                theme={theme}
                onMoveLead={onMoveLead}
                onUpdateLead={onUpdateLead}
                onDeleteLead={onDeleteLead}
                onDragEnd={handleDragEnd}
                isDragOver={draggedOverStage === originalStageIndex}
                onDragOver={() => setDraggedOverStage(originalStageIndex)}
                onDragLeave={() => setDraggedOverStage(null)}
              />
            );
          })}
        </XStack>
      </ScrollView>
    </YStack>
  )
})

// Funnel Column Component - Each stage gets its own column
const FunnelColumn: React.FC<{
  stage: any,
  stageIndex: number,
  leads: any[],
  theme: boolean,
  onMoveLead?: (leadId: string, fromStage: string, toStage: string) => void,
  onUpdateLead?: (leadId: string, leadData: any) => void,
  onDeleteLead?: (leadId: string) => void,
  onDragEnd: (leadId: string, fromStageId: string, toStageIndex: number) => void,
  isDragOver: boolean,
  onDragOver: () => void,
  onDragLeave: () => void
}> = ({ stage, stageIndex, leads, theme, onMoveLead, onUpdateLead, onDeleteLead, onDragEnd, isDragOver, onDragOver, onDragLeave }) => {
  const [draggedLead, setDraggedLead] = useState<any>(null);

  const handleDragStart = (lead: any) => {
    setDraggedLead(lead);
  };

  const handleDragEnd = () => {
    if (draggedLead) {
      onDragEnd(draggedLead._id, stage._id, stageIndex);
      setDraggedLead(null);
    }
  };

  return (
    <View style={{ width: 280, height: '70%', minHeight: Dimensions.get('window').height * 0.7 }}>
      {/* Stage Header */}
      <Card
        style={{
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderRadius: 16,
          marginBottom: 12,
          padding: 16,
          borderWidth: isDragOver ? 2 : 1,
          borderColor: isDragOver
            ? (theme ? "#3b82f6" : "#60a5fa")
            : (theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)")
        }}
        onTouchStart={onDragOver}
        onTouchEnd={onDragLeave}
      >
        <View style={{ alignItems: 'center' }}>
          <View style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: stage.color || (theme ? "#3b82f6" : "#60a5fa"),
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 8
          }}>
            <Text style={{
              color: 'white',
              fontWeight: '700',
              fontSize: 16
            }}>
              {stageIndex + 1}
            </Text>
          </View>
          <Text style={{
            fontSize: 14,
            fontWeight: '600',
            color: theme ? "#1e293b" : "#f1f5f9",
            textAlign: 'center',
            marginBottom: 4
          }}>
            {parseByDelimiter(stage.title)}
          </Text>
          <Text style={{
            fontSize: 11,
            color: theme ? "#64748b" : "#94a3b8",
            textAlign: 'center',
            marginBottom: 8
          }}>
            {leads.length} lead{leads.length !== 1 ? 's' : ''}
          </Text>
          {stage.description && (
            <Text style={{
              fontSize: 10,
              color: theme ? "#94a3b8" : "#64748b",
              textAlign: 'center',
              lineHeight: 14
            }}>
              {stage.description.slice(0, 50)}...
            </Text>
          )}
        </View>
      </Card>

      {/* Leads in this stage */}
      <ScrollView
        style={{ flex: 1, minHeight: Dimensions.get('window').height * 0.5 }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 20 }}
      >
        {leads.length === 0 ? (
          <Card
            style={{
              padding: 20,
              alignItems: 'center',
              backgroundColor: theme ? "#f8fafc" : "#334155",
              borderColor: theme ? "#e2e8f0" : "#475569",
              borderRadius: 12,
              borderWidth: 1,
              borderStyle: 'dashed'
            }}
          >
            <MaterialIcons
              name="person-add"
              size={24}
              color={theme ? "#94a3b8" : "#64748b"}
              style={{ marginBottom: 8 }}
            />
            <Text style={{
              fontSize: 12,
              color: theme ? "#64748b" : "#94a3b8",
              textAlign: 'center'
            }}>
              No leads in this stage
            </Text>
          </Card>
        ) : (
          leads.map((lead, leadIndex) => (
            <DraggableLeadItem
              key={lead._id || leadIndex}
              lead={lead}
              stageId={stage._id}
              theme={theme}
              onMoveLead={onMoveLead}
              onUpdateLead={onUpdateLead}
              onDeleteLead={onDeleteLead}
              onDragStart={() => handleDragStart(lead)}
              onDragEnd={handleDragEnd}
            />
          ))
        )}
      </ScrollView>
    </View>
  );
};

// Draggable Lead Item Component
const DraggableLeadItem: React.FC<{
  lead: any,
  stageId: string,
  theme: boolean,
  onMoveLead?: (leadId: string, fromStage: string, toStage: string) => void,
  onUpdateLead?: (leadId: string, leadData: any) => void,
  onDeleteLead?: (leadId: string) => void,
  onDragStart: () => void,
  onDragEnd: () => void
}> = ({ lead, stageId, theme, onMoveLead, onUpdateLead, onDeleteLead, onDragStart, onDragEnd }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = () => {
    setIsDragging(true);
    onDragStart();
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    onDragEnd();
  };

  return (
    <View style={{ marginBottom: 8 }}>
      <Card
        style={{
          backgroundColor: isDragging
            ? (theme ? "#fef3c7" : "#451a03")
            : (theme ? "#ffffff" : "#1e293b"),
          borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
          borderRadius: 12,
          padding: 12,
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: isDragging ? 0.2 : 0.1,
          shadowRadius: isDragging ? 8 : 4,
          elevation: isDragging ? 8 : 4,
          transform: [{ scale: isDragging ? 1.02 : 1 }]
        }}
      >
        <XStack alignItems="center" justifyContent="space-between">
          <XStack alignItems="center" flex={1}>
            <View style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              backgroundColor: theme ? "#e5e7eb" : "#374151",
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 10
            }}>
              <MaterialIcons
                name="person"
                size={16}
                color={theme ? "#6b7280" : "#9ca3af"}
              />
            </View>
            <YStack flex={1}>
              <Text style={{
                fontSize: 14,
                fontWeight: '600',
                color: theme ? "#1e293b" : "#f1f5f9",
                marginBottom: 2
              }}>
                {lead.data?.name || 'Unknown Lead'}
              </Text>
              <Text style={{
                fontSize: 12,
                color: theme ? "#64748b" : "#94a3b8"
              }}>
                {lead.data?.email || 'No email'}
              </Text>
            </YStack>
          </XStack>

          <XStack alignItems="center" gap={6}>
            <Pressable
              onPressIn={handleDragStart}
              onPressOut={handleDragEnd}
              style={{
                padding: 6,
                backgroundColor: theme ? "#f3f4f6" : "#374151",
                borderRadius: 6
              }}
            >
              <MaterialIcons
                name="drag-indicator"
                size={16}
                color={theme ? "#6b7280" : "#9ca3af"}
              />
            </Pressable>

            <Pressable
              style={{
                padding: 6,
                backgroundColor: theme ? "#dbeafe" : "#1e40af",
                borderRadius: 6
              }}
            >
              <MaterialIcons
                name="more-vert"
                size={16}
                color={theme ? "#1e40af" : "#93c5fd"}
              />
              </Pressable>
            </XStack>
          </XStack>
        </Card>
      </View>
    );
  };

export default SalesFunnelList;
